import React from "react";
import PropTypes from "prop-types";
import { Box, Typography, Stack } from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { colors, borderRadiusPx } from "../../../../../theme";
import CommonDialog from "../../../../Components/ui/overlays/CommonDialog";
import CommonButton from "../../../../Components/ui/inputs/CommonButton";
import FiveSlotSymbolIcon from "./FiveSlotSymbolIcon";

export const FiveSlotWinModal = ({
  open,
  onClose,
  result,
  currencySymbol = "BET",
  betAmount = 0,
  onSpinAgain,
}) => {
  if (!result) return null;

  const isWin = Boolean(result.isWin && Number(result.amount || 0) > 0);
  const isJackpot = Boolean(isWin && (result.type === "jackpot" || result.jackpotTier));
  const isFreeSpins = Boolean(isWin && result.type === "freespins");

  const betLost = parseFloat(result?.betAmount ?? betAmount ?? 0);
  const betLostFormatted = (betLost > 0 ? betLost : 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // Title calculation
  let title = "You Lost This Bet";
  if (isJackpot) {
    if (result.jackpotTier === "mega") title = "Mega Jackpot Winner!";
    else if (result.jackpotTier === "major") title = "Major Jackpot Winner!";
    else if (result.jackpotTier === "mini") title = "Mini Jackpot Winner!";
    else title = "Jackpot Winner!";
  } else if (isFreeSpins) {
    title = "Free Spins Won!";
  } else if (isWin) {
    title = "Winning Spin!";
  }

  // Message calculation
  let message = "The reels did not land a winning combination on the payline.";
  if (isJackpot) {
    message = `Congratulations! You hit the ${
      result.jackpotTier ? result.jackpotTier.toUpperCase() : "PROGRESSIVE"
    } Jackpot on-chain!`;
  } else if (isFreeSpins) {
    message = `${result.freeSpinsWon || 5} Free Spins awarded!`;
  } else if (isWin) {
    message = "Congratulations! You landed a winning payout on the payline!";
  }

  // Description calculation
  let description = `Your bet of ${betLostFormatted} ${currencySymbol} has been deducted from your wallet balance. Spin again to hit the Mega, Major, or Mini Jackpots!`;
  if (isWin) {
    description = "Your winnings have been credited to your wallet balance.";
  }

  return (
    <CommonDialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      dividers={false}
      showCloseButton={true}
      PaperProps={{
        sx: {
          borderRadius: borderRadiusPx.xl,
          p: { xs: 2, sm: 2.5 },
          textAlign: "center",
          bgcolor: "#ffffff",
          boxShadow: "0 20px 45px rgba(0, 0, 0, 0.15)",
        },
      }}
    >
      <Box sx={{ pt: 1, pb: 1, px: { xs: 1, sm: 2 } }}>
        {/* Header Icon Badge - Matching Platform Popups */}
        <Box
          sx={{
            width: 68,
            height: 68,
            borderRadius: borderRadiusPx.circle,
            background: isJackpot
              ? "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
              : isWin
              ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
              : "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
            color: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 2,
            boxShadow: isJackpot
              ? "0 8px 22px rgba(245, 158, 11, 0.4)"
              : isWin
              ? "0 8px 22px rgba(16, 185, 129, 0.35)"
              : "0 8px 22px rgba(239, 68, 68, 0.35)",
            animation: isWin ? "modalBadgePulse 1.2s infinite alternate ease-in-out" : "none",
            "@keyframes modalBadgePulse": {
              "0%": { transform: "scale(1)" },
              "100%": { transform: "scale(1.08)" },
            },
          }}
        >
          {isJackpot ? (
            <EmojiEventsIcon sx={{ fontSize: 38 }} />
          ) : isWin ? (
            <AutoAwesomeIcon sx={{ fontSize: 38 }} />
          ) : (
            <HighlightOffIcon sx={{ fontSize: 38 }} />
          )}
        </Box>

        {/* Title */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: 800,
            color: isJackpot
              ? colors.amber[800]
              : isWin
              ? colors.emerald[700]
              : "#dc2626",
            mb: 1,
            lineHeight: 1.2,
          }}
        >
          {title}
        </Typography>

        {/* Message */}
        <Typography
          variant="body1"
          sx={{
            color: colors.slate[700],
            fontWeight: 600,
            lineHeight: 1.45,
            mb: 1,
          }}
        >
          {message}
        </Typography>

        {/* Description */}
        <Typography
          variant="body2"
          sx={{
            color: colors.slate[400],
            lineHeight: 1.45,
            mb: 2,
          }}
        >
          {description}
        </Typography>

        {/* Payout or Loss Display Box */}
        {isWin ? (
          <Box
            sx={{
              p: 2,
              mb: 2,
              borderRadius: borderRadiusPx.lg,
              bgcolor: isJackpot ? "#fffbeb" : "#ecfdf5",
              border: isJackpot ? "1.5px solid #fde68a" : "1.5px solid #a7f3d0",
              boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: isJackpot ? "#b45309" : "#047857",
                fontWeight: 800,
                letterSpacing: "0.8px",
                textTransform: "uppercase",
                display: "block",
              }}
            >
              {isJackpot ? "JACKPOT PAYOUT" : "PRIZE PAYOUT"}
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 900,
                color: isJackpot ? "#b45309" : "#059669",
                fontFamily: "monospace",
                mt: 0.5,
              }}
            >
              +{parseFloat(result.amount || 0).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}{" "}
              {currencySymbol}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              p: 2,
              mb: 2,
              borderRadius: borderRadiusPx.lg,
              bgcolor: "#fef2f2",
              border: "1.5px solid #fecaca",
              boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "#b91c1c",
                fontWeight: 800,
                letterSpacing: "0.8px",
                textTransform: "uppercase",
                display: "block",
              }}
            >
              BET AMOUNT LOST
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 900,
                color: "#dc2626",
                fontFamily: "monospace",
                mt: 0.5,
              }}
            >
              -{betLostFormatted} {currencySymbol}
            </Typography>
          </Box>
        )}

        {/* Payline Symbols Row */}
        {result.symbols && Array.isArray(result.symbols) && result.symbols.length > 0 && (
          <Box sx={{ mb: 2.5 }}>
            <Typography
              variant="caption"
              sx={{
                color: colors.slate[400],
                fontWeight: 600,
                display: "block",
                mb: 1,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              {isWin ? "Winning Combination" : "Resulting Symbols"}
            </Typography>
            <Stack
              direction="row"
              spacing={1.5}
              justifyContent="center"
              alignItems="center"
            >
              {result.symbols.map((sym, idx) => (
                <Box
                  key={idx}
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "10px",
                    bgcolor: isWin ? "#f8fafc" : "#fef2f2",
                    border: isWin ? "1.5px solid #cbd5e1" : "1.5px solid #fecaca",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                  }}
                >
                  <FiveSlotSymbolIcon symbol={sym} size="sm" isWinning={isWin} />
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {/* Action Buttons */}
        <Box sx={{ mt: 1 }}>
          {isWin ? (
            <CommonButton
              fullWidth
              variant="primary"
              size="large"
              onClick={onClose}
              label="Collect & Continue"
              sx={{
                py: 1.35,
                fontWeight: 700,
                fontSize: "0.98rem",
                borderRadius: borderRadiusPx.lg,
              }}
            />
          ) : (
            <Stack direction="row" spacing={1.5}>
              <CommonButton
                fullWidth
                variant="refresh"
                size="large"
                onClick={onClose}
                label="Close"
                sx={{
                  py: 1.35,
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  borderRadius: borderRadiusPx.lg,
                }}
              />
              <CommonButton
                fullWidth
                variant="primary"
                size="large"
                onClick={() => {
                  onClose();
                  if (onSpinAgain) {
                    onSpinAgain();
                  }
                }}
                label="Spin Again"
                sx={{
                  py: 1.35,
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  borderRadius: borderRadiusPx.lg,
                }}
              />
            </Stack>
          )}
        </Box>
      </Box>
    </CommonDialog>
  );
};

FiveSlotWinModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  result: PropTypes.shape({
    isWin: PropTypes.bool,
    amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    betAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    type: PropTypes.string,
    jackpotTier: PropTypes.string,
    label: PropTypes.string,
    symbols: PropTypes.array,
    freeSpinsWon: PropTypes.number,
  }),
  currencySymbol: PropTypes.string,
  betAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onSpinAgain: PropTypes.func,
};

export default FiveSlotWinModal;
